import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import QuestionFormOnlyText from "./question_form/QuestionFormOnlyText";
import QuestionFormTest from "./question_form/QuestionFormTest";
import QuestionFormDropdown from "./question_form/QuestionFormDropdown";
import QuestionFormDnD from "./question_form/QuestionFormDnD";
import QuestionFormVideo from "./question_form/QuestionFormVideo";

const safeStr = (v) => (v == null ? "" : String(v));
const ensureArray = (v) => (Array.isArray(v) ? v : []);

const makeId = () => Math.floor(Math.random() * 1_000_000);

const detectKind = (q) => {
    if (!q) return "test";

    const t = String(q.type || "").toLowerCase();
    if (t === "text") return "text";
    if (t === "video") return "video";
    if (t === "dnd") return "dnd";
    if (t === "dropdown") return "dropdown";
    if (t === "test") return "test";

    // Legacy fallback for existing data.
    if (q.onlyText) return "text";
    if (q.isGame) return "dnd";
    if (q.type === "interactive" && Array.isArray(q.initialWords)) return "dnd";
    if (q.type === "interactive") return "dropdown";
    if (q.isTest) return "test";

    return "dropdown";
};

const kindLabel = (kind) => {
    if (kind === "text") return "Text";
    if (kind === "test") return "Test";
    if (kind === "dropdown") return "Dropdown";
    if (kind === "dnd") return "DnD";
    if (kind === "video") return "Video";
    return "Question";
};

const sanitizeQuestionByKind = (q, kind) => {
    const id = q?.id ?? makeId();

    if (kind === "text") {
        return {
            id,
            type: "text",
            subtitle: safeStr(q?.subtitle),
            text: safeStr(q?.text || "<p>Текст завдання (можна html)...</p>"),
        };
    }

    if (kind === "test") {
        return {
            id,
            question: safeStr(q?.question || "Запитання"),
            options: ensureArray(q?.options).length
                ? ensureArray(q?.options)
                : [
                    { id: "А", text: "Варіант А" },
                    { id: "Б", text: "Варіант Б" },
                    { id: "В", text: "Варіант В" },
                    { id: "Г", text: "Варіант Г" },
                ],
            correctAnswer: safeStr(q?.correctAnswer || "А"),
            type: "test",
            subtitle: safeStr(q?.subtitle),
            imgPath: safeStr(q?.imgPath),
            videoPath: safeStr(q?.videoPath),
            addClass: safeStr(q?.addClass),
        };
    }

    if (kind === "dropdown") {
        return {
            id,
            question: safeStr(q?.question || "Заповніть пропущені слова:"),
            dropdownQuestionParts: ensureArray(q?.dropdownQuestionParts).length
                ? ensureArray(q?.dropdownQuestionParts)
                : ["Тут ", { id: "dropdown1", text: " ..." }, " і ", { id: "dropdown2", text: " ..." }, "."],
            options: ensureArray(q?.options).length
                ? ensureArray(q?.options)
                : [
                    { id: "А", text: "Варіант А" },
                    { id: "Б", text: "Варіант Б" },
                    { id: "В", text: "Варіант В" },
                    { id: "Г", text: "Варіант Г" },
                ],
            correctAnswers:
                q?.correctAnswers && typeof q.correctAnswers === "object" ? q.correctAnswers : { dropdown1: "А", dropdown2: "Б" },
            showAnswerOptions: Boolean(q?.showAnswerOptions ?? true),
            dropdownAmount: Number(q?.dropdownAmount ?? 2) || 0,
            type: "dropdown",
            subtitle: safeStr(q?.subtitle),
        };
    }

    if (kind === "dnd") {
        return {
            id,
            question: safeStr(q?.question || "Sort words into buckets"),
            type: "dnd",
            initialWords: ensureArray(q?.initialWords).length
                ? ensureArray(q?.initialWords)
                : [
                    { id: 1, text: "Word 1", type: "leftBucket" },
                    { id: 2, text: "Word 2", type: "rightBucket" },
                ],
            leftBucketTitle: safeStr(q?.leftBucketTitle || "Left bucket"),
            rightBucketTitle: safeStr(q?.rightBucketTitle || "Right bucket"),
            centerBucketTitle: safeStr(q?.centerBucketTitle),
            bucketsAmount: Number(q?.bucketsAmount ?? 2) || 0,
            subtitle: safeStr(q?.subtitle),
        };
    }

    return {
        id,
        question: safeStr(q?.question || "<b>Watch the video and complete the task.</b>"),
        videoPath: safeStr(q?.videoPath || "https://www.youtube.com/embed/"),
        addClass: safeStr(q?.addClass || "horizontal"),
        shouldGoNext: Boolean(q?.shouldGoNext ?? true),
        type: "video",
        isCreativeTask: Boolean(q?.isCreativeTask ?? false),
        subtitle: safeStr(q?.subtitle),
    };
};

const stripHtml = (s) => safeStr(s).replace(/<[^>]*>/g, "").trim();

const TypeChips = ({ kind, q }) => {
    const chips = [kindLabel(kind)];
    if (kind === "video" && q?.isCreativeTask) chips.push("creative");

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {chips.map((c, idx) => (
                <Chip key={`${c}-${idx}`} label={c} size="small" />
            ))}
        </Stack>
    );
};

const ModuleQuestionsEditorDialog = ({ open, onClose, initialQuestions, onSave, title }) => {
    const [questions, setQuestions] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        const arr = ensureArray(initialQuestions).map((q) => {
            const kind = detectKind(q);
            return sanitizeQuestionByKind({ ...q, id: q?.id ?? makeId() }, kind);
        });
        setQuestions(arr);
        setSelectedIdx(0);
    }, [initialQuestions, open]);

    const selected = useMemo(() => questions[selectedIdx] || null, [questions, selectedIdx]);
    const selectedKind = useMemo(() => detectKind(selected), [selected]);

    const updateSelected = (nextValue) => {
        setQuestions((prev) => prev.map((q, idx) => (idx === selectedIdx ? nextValue : q)));
    };

    const addQuestion = (kind) => {
        const q = sanitizeQuestionByKind({}, kind);
        setQuestions((prev) => [...prev, q]);
        setSelectedIdx(questions.length);
    };

    const removeSelected = () => {
        setQuestions((prev) => prev.filter((_, idx) => idx !== selectedIdx));
        setSelectedIdx((i) => Math.max(0, i - 1));
    };

    const moveSelected = (dir) => {
        setQuestions((prev) => {
            const i = selectedIdx;
            const j = i + dir;
            if (i < 0 || j < 0 || i >= prev.length || j >= prev.length) return prev;
            const next = [...prev];
            const tmp = next[i];
            next[i] = next[j];
            next[j] = tmp;
            return next;
        });
        setSelectedIdx((i) => i + dir);
    };

    const handleSave = () => {
        const normalized = questions.map((q) => {
            const kind = detectKind(q);
            return sanitizeQuestionByKind(q, kind);
        });
        onSave(normalized);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {title || "Контент модуля"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Питань: {questions.length}
                        </Typography>
                    </Box>

                    <Box>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>
                            Додати
                        </Button>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("text"); }}>Текст/Презентація</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("test"); }}>Тест</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("dropdown"); }}>Дропдаун</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("dnd"); }}>DnD</MenuItem>
                            <MenuItem onClick={() => { setAnchorEl(null); addQuestion("video"); }}>Відео</MenuItem>
                        </Menu>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
                    <Box
                        sx={{
                            width: { xs: "100%", md: 360 },
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Список
                            </Typography>
                        </Box>

                        <Box sx={{ maxHeight: 520, overflow: "auto" }}>
                            {questions.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Typography color="text.secondary">Порожньо</Typography>
                                </Box>
                            ) : (
                                questions.map((q, idx) => {
                                    const active = idx === selectedIdx;
                                    const kind = detectKind(q);
                                    const preview = kind === "text" ? stripHtml(q?.text).slice(0, 40) : stripHtml(q?.question).slice(0, 40);

                                    return (
                                        <Box
                                            key={q.id || idx}
                                            onClick={() => setSelectedIdx(idx)}
                                            sx={{
                                                p: 1.5,
                                                cursor: "pointer",
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor: active ? "action.selected" : "transparent",
                                            }}
                                        >
                                            <Stack spacing={0.5}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Typography fontWeight={700} variant="body2">
                                                        #{idx + 1} {kindLabel(kind)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        id: {q.id}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    {preview || "-"}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        {!selected ? (
                            <Box sx={{ p: 2 }}>
                                <Typography color="text.secondary">Додай питання, щоб редагувати</Typography>
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                        <Box>
                                            <Typography fontWeight={700}>Питання #{selectedIdx + 1}</Typography>
                                            <TypeChips kind={selectedKind} q={selected} />
                                        </Box>

                                        <Stack direction="row" spacing={1}>
                                            <IconButton size="small" onClick={() => moveSelected(-1)} disabled={selectedIdx <= 0}>
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => moveSelected(1)} disabled={selectedIdx >= questions.length - 1}>
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={removeSelected} disabled={questions.length === 0}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>

                                    <Divider sx={{ my: 2 }} />

                                    <Stack spacing={2}>
                                        <TextField
                                            label="id"
                                            value={safeStr(selected.id)}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const num = Number(raw);
                                                updateSelected({ ...selected, id: Number.isNaN(num) ? raw : num });
                                            }}
                                            fullWidth
                                        />

                                        {selectedKind === "text" && (
                                            <QuestionFormOnlyText value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "test" && (
                                            <QuestionFormTest value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "dropdown" && (
                                            <QuestionFormDropdown value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "dnd" && (
                                            <QuestionFormDnD value={selected} onChange={updateSelected} />
                                        )}

                                        {selectedKind === "video" && (
                                            <QuestionFormVideo value={selected} onChange={updateSelected} />
                                        )}
                                    </Stack>
                                </Box>

                                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                        JSON (перевірка)
                                    </Typography>
                                    <TextField
                                        value={JSON.stringify(selected, null, 2)}
                                        fullWidth
                                        multiline
                                        minRows={12}
                                        InputProps={{ readOnly: true }}
                                        sx={{ fontFamily: "monospace" }}
                                    />
                                </Box>
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>Скасувати</Button>
                <Button variant="contained" onClick={handleSave}>
                    Зберегти
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModuleQuestionsEditorDialog;
